<template>
  <header class="edu-navbar">
    <div class="edu-navbar__inner">
      <div class="edu-navbar__left">
        <Transition v-if="isMobile" name="icon-fade" mode="out-in">
          <VaIcon
            :name="isSidebarMinimized ? 'menu' : 'close'"
            size="24px"
            class="text-white mt-[3px] cursor-pointer"
            @click="isSidebarMinimized = !isSidebarMinimized"
          />
        </Transition>
        <RouterLink :to="{ name: 'education-home' }" class="inline-flex items-center">
          <img
            src="/logo-education.png?v=3"
            class="h-12 object-contain cursor-pointer"
            alt="Pacific Link Education"
          />
        </RouterLink>
      </div>

      <nav v-if="!isMobile" class="edu-navbar__right" aria-label="Education navigation">
        <template v-for="item in navItems" :key="item.label">
          <div v-if="item.children?.length" class="edu-nav-dropdown">
            <RouterLink
              :to="{ name: 'education-programs' }"
              class="edu-nav-link edu-nav-dropdown__trigger"
              active-class=""
              exact-active-class=""
            >
              {{ item.label }}
              <span class="edu-nav-dropdown__caret" aria-hidden="true">▾</span>
            </RouterLink>
            <div class="edu-nav-dropdown__menu" role="menu">
              <RouterLink
                v-for="child in item.children"
                :key="child.type"
                :to="{ name: 'education-programs', query: { type: child.type } }"
                class="edu-nav-dropdown__item"
                role="menuitem"
                active-class=""
                exact-active-class=""
              >
                {{ child.label }}
              </RouterLink>
            </div>
          </div>
          <RouterLink
            v-else-if="item.name"
            :to="{ name: item.name }"
            class="edu-nav-link"
            active-class=""
            exact-active-class=""
          >
            {{ item.label }}
          </RouterLink>
        </template>
      </nav>
    </div>
  </header>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useGlobalStore } from '../../stores/global-store'

defineProps({
  isMobile: { type: Boolean, default: false },
})

const GlobalStore = useGlobalStore()
const { isSidebarMinimized } = storeToRefs(GlobalStore)

const navItems = [
  { name: 'education-home', label: '首页' },
  {
    label: '项目',
    children: [
      { type: 'masters', label: '硕士项目' },
      { type: 'doctoral', label: '博士项目' },
      { type: 'pathway', label: '硕博连读' },
    ],
  },
  { name: 'education-university', label: '大学认证' },
  { name: 'education-how-it-works', label: '申请流程' },
  { name: 'education-faq', label: '常见问题' },
  { name: 'education-contact', label: '咨询' },
] as const
</script>

<style lang="scss" scoped>
.edu-navbar {
  z-index: 2;
  height: 5rem;
  background: #064e8a;
  display: flex;
  align-items: center;
}

.edu-navbar__inner {
  width: 100%;
  padding: 0 10%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.edu-navbar__left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}

.edu-navbar__right {
  display: flex;
  align-items: center;
  gap: 0.15rem;
  flex-shrink: 0;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.edu-nav-link {
  color: rgba(255, 255, 255, 0.92);
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 0.4rem 0.65rem;
  border-radius: 6px;
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease;
  background: transparent;
  border: 0;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background: rgba(0, 210, 255, 0.18);
    color: #fff;
  }
}

.edu-nav-dropdown {
  position: relative;

  &:hover .edu-nav-dropdown__menu {
    visibility: visible;
    opacity: 1;
    pointer-events: auto;
  }

  &:hover .edu-nav-dropdown__trigger {
    background: rgba(0, 210, 255, 0.18);
    color: #fff;
  }
}

.edu-nav-dropdown__trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.edu-nav-dropdown__caret {
  font-size: 0.7rem;
  opacity: 0.85;
}

.edu-nav-dropdown__menu {
  position: absolute;
  top: calc(100% - 2px);
  left: 0;
  min-width: 9.5rem;
  padding: 0.4rem;
  border-radius: 10px;
  background: #053d6d;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.22);
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  z-index: 20;
  visibility: hidden;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.12s ease, visibility 0.12s ease;

  /* Keep hover alive while moving from the trigger into the menu. */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: -0.55rem;
    height: 0.55rem;
  }
}

.edu-nav-dropdown__item {
  color: rgba(255, 255, 255, 0.95);
  text-decoration: none;
  font-size: 0.88rem;
  font-weight: 600;
  padding: 0.55rem 0.75rem;
  border-radius: 7px;
  white-space: nowrap;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(0, 210, 255, 0.18);
  }
}

.icon-fade-enter-active,
.icon-fade-leave-active {
  transition: transform 0.5s ease;
}

.icon-fade-enter,
.icon-fade-leave-to {
  transform: scale(0.5);
}
</style>
